import { useState, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

// Third Party
import {
	useMutation,
	useQueryClient,
	useQuery,
	useQueries,
} from "@tanstack/react-query";
import { Formik, Field } from "formik";

// UI
import HeadingTitleItem from "@/components/HeadingTitleItem";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import DatePicker from "@/components/ui/DatePicker";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

// Icons
import { Upload, ArrowLeft } from "lucide-react";

// Services
import { updateSOE } from "@/services/soe";
import { getDistrict } from "@/services/master";
import type { Sector } from "@/types/master";
import { handleUploadFile } from "@/services/presign-url";
import type { MutateSOE, SOE } from "@/types/soe";
import { S3_BUCKET_LINK } from "@/components/constants";
import formatDate from "@/utils/formatDate";
import {
	getProvinces,
	getMinistries,
	getDepartments,
	getEconomicRegions,
	getCoreSectors as getSectors,
} from "@/services/master";
import type {
	Provinces,
	Ministry,
	Department,
	EconomicRegion,
	CoreSector,
} from "@/types/master";

export const Route = createFileRoute(
	"/_layout-private/account-management/soe/_information/general/edit/",
)({
	component: EditGeneralInformation,
	validateSearch: (search: Record<string, unknown>) => {
		return {
			data: search.data as string | undefined,
		};
	},
});

function EditGeneralInformation() {
	const parentId = String(localStorage.getItem("companyId"));
	const id = parentId.replace(/^"(.+)"$/, "$1");

	// const { id } = Route.useParams();
	const { data } = Route.useSearch();

	const { toast } = useToast();
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	// States
	const [parsedData, setParsedData] = useState({} as SOE);
	const [selectedFile, setSelectedFile] = useState("");
	const [selectedProvince, setSelectedProvince] = useState("");

	const [dependOn, setDependOn] = useState("MINISTRY");
	const [addOnValue, setAddOnValue] = useState({
		dependOfProvinceId: "",
		dependOfDepartmentId: "",
		ministryId: "",
	});
	const [confirmPassword, setConfirmPassword] = useState("");
	const [confirmPasswordError, setConfirmPasswordError] = useState(false);

	// Query
	// const [{ data: provincesRes }, { data: sectorsRes }] = useQueries({
	// 	queries: [
	// 		{
	// 			queryKey: ["provinces"],
	// 			queryFn: getProvinces,
	// 		},
	// 		{
	// 			queryKey: ["sectors"],
	// 			queryFn: getSectors,
	// 		},
	// 	],
	// });

	const [
		{ data: provincesRes },
		{ data: departmentsRes },
		{ data: ministriesRes },
		{ data: economicRegionsRes },
		{ data: sectorsRes },
	] = useQueries({
		queries: [
			{
				queryKey: ["provinces"],
				queryFn: getProvinces,
			},
			{
				queryKey: ["departments"],
				queryFn: () => getDepartments(),
			},
			{
				queryKey: ["ministries"],
				queryFn: () => getMinistries(),
			},
			{
				queryKey: ["economic-regions"],
				queryFn: () => getEconomicRegions(),
			},
			{
				queryKey: ["sector"],
				queryFn: () => getSectors(),
			},
		],
	});

	const { data: districtsRes } = useQuery({
		queryKey: ["district", selectedProvince],
		queryFn: () => getDistrict(selectedProvince),
		enabled: !!selectedProvince,
	});

	// Mutations
	const updateMutation = useMutation<unknown, unknown, MutateSOE>({
		mutationFn: (formData) => updateSOE(id, formData),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["soe", id] });
			toast({
				title: "ສຳເລັດ",
				description: "ແກ້ໄຂບັນຊີສໍາເລັດແລ້ວ",
			});
			navigate({ to: "..", replace: true });
		},
		onError: (error) => {
			console.log(error);

			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ມີບາງຢ່າງຜິດພາດ, ກະລຸນາລອງໃໝ່ ",
				variant: "destructive",
			});
		},
	});

	const uploadMutation = useMutation({
		mutationFn: handleUploadFile,
		onSuccess: (filename) => {
			console.log("Upload successful, filename:", filename);

			setSelectedFile(filename);

			toast({
				title: "ສຳເລັດ",
				description: "ອັບໂຫຼດຂໍ້ມູນສໍາເລັດແລ້ວ",
			});
		},
		onError: (error) => {
			console.error("Upload failed:", error);

			const fileInput = document.getElementById("fileUrl") as HTMLInputElement;
			if (fileInput) fileInput.value = "";

			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ມີບາງຢ່າງຜິດພາດ, ກະລຸນາລອງໃໝ່wwwwww",
				variant: "destructive",
			});
		},
	});

	// Effects
	useEffect(() => {
		if (data) {
			const parseData = JSON.parse(data);

			console.log(JSON.parse(data), "Data");
			setParsedData(parseData);
			setSelectedProvince(parseData?.province?.provinceCode);
		}
	}, [data]);

	// Data
	const province = provincesRes?.data as Provinces[];
	const department = departmentsRes?.data as Department[];
	const ministry = ministriesRes?.data as Ministry[];
	const economicRegion = economicRegionsRes?.data as EconomicRegion[];
	const sector = sectorsRes?.data as CoreSector[];

	return (
		// <div className="space-y-[32px]">
		// 	<HeadingTitleItem
		// 		title="ຂໍ້ມູນທົ່ວໄປ"
		// 		subTitle="ສິ່ງທີ່ທ່ານປ້ອນໄປຈະຖືກນຳໄປບັນທຶກເປັນຂໍ້ມູນລັດວິສາຫະກິດ"
		// 	/>

		// 	<Formik
		// 		enableReinitialize
		// 		initialValues={{
		// 			companyName: parsedData?.companyName || "",
		// 			govShareholding: parsedData?.govShareholding || "",
		// 			registrationId: parsedData?.registrationId || "",
		// 			sectorId: parsedData?.sectorId || "",
		// 			establishedDate:
		// 				formatDate({
		// 					date: parsedData?.establishedDate as string,
		// 					format: "YYYY-MM-DD",
		// 				}) || "",
		// 			empCount: parsedData?.empCount || "",
		// 			provinceId: parsedData?.provinceId || "",
		// 			districtId: parsedData?.districtId || "",
		// 			village: parsedData?.village || "",
		// 		}}
		// 		onSubmit={(values: MutateSOE) => {
		// 			console.log(values);

		// 			const prepareData = {
		// 				...values,
		// 				govShareholding: Number(values.govShareholding),
		// 				companyProfileUrl: selectedFile || parsedData?.companyProfileUrl,
		// 			};

		// 			updateMutation.mutate(prepareData);
		// 		}}
		// 	>
		// 		{({ handleSubmit, values, setFieldValue }) => (
		// 			<form onSubmit={handleSubmit} className="space-y-[24px]">
		// 				<div>
		// 					<Label htmlFor="profile_image_url">ຮູບພາບ</Label>
		// 					<div className="w-[200px] h-[200px] rounded-full border border-dotted bg-zinc-50 border-zinc-200 overflow-hidden  relative flex items-center flex-col justify-center gap-[24px]">
		// 						{selectedFile ? (
		// 							<img
		// 								alt="pre "
		// 								src={S3_BUCKET_LINK + selectedFile}
		// 								className="object-cover w-full h-full"
		// 							/>
		// 						) : uploadMutation.isPending ? (
		// 							<p>ກຳລັງອັບໂຫຼດຮູບພາບ...</p>
		// 						) : (
		// 							<>
		// 								<Upload className="w-8 h-8 " />
		// 								<div className="text-center text-[14px]">
		// 									<p>ອັບໂຫຼດຮູບພາບ</p>
		// 									<p className="w-full text-muted-foreground text-wrap">
		// 										(ຂະໜາດບໍ່ເກີນ 10MB)
		// 									</p>
		// 								</div>{" "}
		// 							</>
		// 						)}

		// 						<input
		// 							type="file"
		// 							name="fileUrl"
		// 							id="fileUrl"
		// 							accept="image/png, image/jpg, image/jpeg"
		// 							className="absolute inset-0 opacity-0 cursor-pointer"
		// 							onChange={(e) => {
		// 								if (e.target.files) {
		// 									uploadMutation.mutate(e);
		// 								}
		// 							}}
		// 						/>
		// 					</div>
		// 				</div>
		// 				<div className="[&>*]:w-full flex items-center justify-between gap-[32px] ">
		// 					<div>
		// 						<Label>
		// 							ເລກລັດວິສາຫະກິດ <span className="text-red-500">*</span>
		// 						</Label>
		// 						<Field as={Input} name="registrationId" required disabled />
		// 					</div>
		// 					<div>
		// 						<Label>
		// 							ເລກອາກອນ <span className="text-red-500">*</span>
		// 						</Label>
		// 						<Field as={Input} required disabled />
		// 					</div>
		// 				</div>

		// 				<div>
		// 					<Label>
		// 						ຊື່ລັດວິສາຫະກິດ <span className="text-red-500">*</span>
		// 					</Label>
		// 					<Field as={Input} required name="companyName" />
		// 				</div>

		// 				<div>
		// 					<Label>
		// 						ລັດຖືຫຸ້ນ <span className="text-red-500">*</span>
		// 					</Label>
		// 					<Field as={Input} required name="govShareholding" />
		// 				</div>

		// 				<div>
		// 					<Label htmlFor="sectorId">
		// 						ຂະແໜງການ <span className="text-red-500">*</span>
		// 					</Label>
		// 					<Select
		// 						name="sectorId"
		// 						onValueChange={(value) => {
		// 							setFieldValue("sectorId", value);
		// 						}}
		// 						value={values.sectorId}
		// 						required
		// 					>
		// 						<SelectTrigger className="w-full">
		// 							<SelectValue placeholder="ເລືອກຂະແໜງການ" />
		// 						</SelectTrigger>
		// 						<SelectContent>
		// 							{sector?.map((item) => (
		// 								<SelectItem value={item.id} key={item.id}>
		// 									{item?.sector}
		// 								</SelectItem>
		// 							))}
		// 						</SelectContent>
		// 					</Select>
		// 				</div>

		// 				<div>
		// 					<Label>
		// 						ຈຳນວນພະນັກງານ <span className="text-red-500">*</span>
		// 					</Label>
		// 					<Field as={Input} required name="empCount" type="number" />
		// 				</div>

		// 				<DatePicker
		// 					label="ວັນທີສ້າງຕັ້ງ"
		// 					value={values.establishedDate}
		// 					onChange={(value) => setFieldValue("establishedDate", value)}
		// 				/>

		// 				<div className="grid grid-cols-3 gap-4">
		// 					<div>
		// 						<Label htmlFor="provinceId">ແຂວງ</Label>
		// 						<Select
		// 							name="provinceId"
		// 							onValueChange={(value) => {
		// 								setFieldValue("provinceId", value);
		// 								// Move the province code setting here
		// 								const selectedProvinceData = province?.find(
		// 									(province) => province.id === value,
		// 								);
		// 								if (selectedProvinceData) {
		// 									setSelectedProvince(selectedProvinceData.provinceCode);
		// 								}
		// 							}}
		// 							value={values.provinceId}
		// 						>
		// 							<SelectTrigger className="w-full">
		// 								<SelectValue placeholder="ເລືອກແຂວງ" />
		// 							</SelectTrigger>
		// 							<SelectContent>
		// 								{province?.map((province) => (
		// 									<SelectItem
		// 										value={province.id}
		// 										key={province.provinceCode}
		// 									>
		// 										{province?.province}
		// 									</SelectItem>
		// 								))}
		// 							</SelectContent>
		// 						</Select>
		// 					</div>
		// 					<div>
		// 						<Label htmlFor="districtId">ເມືອງ</Label>
		// 						<Select
		// 							name="districtId"
		// 							onValueChange={(value) => setFieldValue("districtId", value)}
		// 							disabled={!selectedProvince}
		// 							value={values.districtId}
		// 						>
		// 							<SelectTrigger className="w-full">
		// 								<SelectValue placeholder="ເລືອກເມືອງ" />
		// 							</SelectTrigger>
		// 							<SelectContent>
		// 								{district?.map((district) => (
		// 									<SelectItem
		// 										value={district.id}
		// 										key={district.districtCode}
		// 									>
		// 										{district?.district}
		// 									</SelectItem>
		// 								))}
		// 							</SelectContent>
		// 						</Select>
		// 					</div>
		// 					<div>
		// 						<Label htmlFor="village">ບ້ານ</Label>

		// 						<Field
		// 							as={Input}
		// 							type="text"
		// 							placeholder="ປ້ອນຊື່ບ້ານ..."
		// 							className="w-full"
		// 							name="village"
		// 						/>
		// 					</div>
		// 				</div>

		// 				<div className="flex items-center justify-center w-full gap-4">
		// 					<Button
		// 						variant={"outline"}
		// 						onClick={() =>
		// 							navigate({
		// 								to: "..",
		// 								replace: true,
		// 							})
		// 						}
		// 					>
		// 						ຍົກເລີກ
		// 					</Button>
		// 					<Button type="submit">ບັນທຶກ</Button>
		// 				</div>
		// 			</form>
		// 		)}
		// 	</Formik>
		// </div>
		<>
			<section className="mt-[32px]">
				<h2 className="text-2xl font-semibold">ເພີ່ມບັນຊີວິສາຫະກິດ</h2>
				<p className="text-muted-foreground">
					ປ້ອນຂໍ້ມູນກ່ຽວກັບກົມ ຫຼື ພະແນກລັດວິສາຫະກິດຄຸ້ມຄອງການລົງທຶນ ແລະ ປະກັນໄພ
				</p>
				<Separator className="my-6" />
				<button
					type="button"
					onClick={() =>
						navigate({
							to: "..",
						})
					}
					className="flex items-center gap-[4px]"
				>
					<ArrowLeft className="w-5 h-5" />
					ກັບຄືນ
				</button>
			</section>
			<Formik
				enableReinitialize
				initialValues={{
					registrationId: parsedData?.registrationId || "",
					economicRegionId: parsedData?.economicRegionId || "",
					companyName: parsedData?.companyName || "",
					userCode: parsedData?.userCode || "",
					password: parsedData?.password || "",
					establishedDate:
						formatDate({
							date: parsedData?.establishedDate,
							format: "YYYY-MM-DD",
						}) || "",
					businessType: parsedData?.businessType || "",
					managementLevel: parsedData?.managementLevel || "",
					coreBusiness: parsedData?.coreBusiness || "",
					coreSectorId: parsedData?.coreSectorId || "",
					purpose: parsedData?.purpose || "",
					partyCommittee: parsedData?.partyCommittee || "",
					govShareholding: parsedData?.govShareholding || "",
					domShareholding: parsedData?.domShareholding || "",
					forShareholding: parsedData?.forShareholding || "",
				}}
				onSubmit={(values: MutateSOE) => {
					setConfirmPasswordError(false);

					if (values.password !== confirmPassword) {
						setConfirmPasswordError(true);
						return;
					}

					if (!values.password) delete values.password;
					if (!values.userCode) delete values.userCode;

					const filteredAddOnValue = Object.fromEntries(
						Object.entries(addOnValue).filter(([, value]) => value !== ""),
					);

					// console.log(addOnValue, "Prep Addon");

					const prepareData = {
						...values,
						companyProfileUrl: selectedFile,
						managementLevel: dependOn,
						...filteredAddOnValue,
					};

					updateMutation.mutate(prepareData);
				}}
			>
				{({ values, handleChange, handleSubmit, setFieldValue }) => (
					<form
						onSubmit={handleSubmit}
						className="max-w-[628px] mx-auto mt-6 space-y-6"
					>
						<section className="space-y-6">
							<header>
								<h3 className="text-[18px] font-semibold">ຂໍ້ມູນທົ່ວໄປ</h3>
								<p className="text-muted-foreground text-[14px]">
									ສິ່ງທີ່ທ່ານປ້ອນໄປຈະຖືກນຳໄປບັນທຶກເປັນຂໍ້ມູນບຸກຄົນ
								</p>
								<Separator className="mt-6" />
							</header>
							<div>
								<Label htmlFor="profile_image_url">ຮູບພາບ</Label>
								<div className="w-[200px] h-[200px] rounded-full border border-dotted bg-zinc-50 border-zinc-200 overflow-hidden  relative flex items-center flex-col justify-center gap-[24px]">
									{selectedFile ? (
										<img
											alt="pre "
											src={S3_BUCKET_LINK + selectedFile}
											className="object-cover w-full h-full"
										/>
									) : uploadMutation.isPending ? (
										<p>ກຳລັງອັບໂຫຼດຮູບພາບ...</p>
									) : (
										<>
											<Upload className="w-8 h-8 " />
											<div className="text-center text-[14px]">
												<p>ອັບໂຫຼດຮູບພາບ</p>
												<p className="w-full text-muted-foreground text-wrap">
													(ຂະໜາດບໍ່ເກີນ 10MB)
												</p>
											</div>{" "}
										</>
									)}

									<input
										type="file"
										name="fileUrl"
										id="fileUrl"
										accept="image/png, image/jpg, image/jpeg"
										className="absolute inset-0 opacity-0 cursor-pointer"
										onChange={(e) => {
											if (e.target.files) {
												uploadMutation.mutate(e);
											}
										}}
									/>
								</div>
							</div>
							<div>
								<Label>
									ເລກປະຈຳຕົວວິສາຫະກິດ <span className="text-red-500">*</span>
								</Label>
								<Field
									as={Input}
									name="registrationId"
									placeholder="ເລກປະຈຳຕົວວິສາຫະກິດ"
									type="text"
									required
								/>
							</div>
							<div>
								<Label>
									ຊື່ວິສາຫະກິດ <span className="text-red-500">*</span>
								</Label>
								<Field
									as={Input}
									name="companyName"
									placeholder="ຊື່ວິສາຫະກິດ"
									type="text"
									required
								/>
							</div>
							<DatePicker
								label="ວັນທີສ້າງຕັ້ງ"
								name="establishedDate"
								id="establishedDate"
								value={values.establishedDate}
								onChange={(value) => {
									setFieldValue("establishedDate", value);
								}}
							/>

							<div>
								<Label htmlFor="economicRegionId">
									ເປົ້າໝາຍຍຸດທະສາດດ້ານ <span className="text-red-500">*</span>
								</Label>
								<Select
									name="economicRegionId"
									onValueChange={(value) => {
										setFieldValue("economicRegionId", value);
									}}
									value={values.economicRegionId}
									required
								>
									<SelectTrigger className="w-full">
										<SelectValue placeholder="ເລືອກເປົ້າໝາຍຍຸດທະສາດດ້ານ" />
									</SelectTrigger>
									<SelectContent>
										{economicRegion?.map((item) => (
											<SelectItem value={item.id} key={item.id}>
												{item?.economicRegion}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<div>
								<Label>ຈຸດປະສົງ</Label>
								<Field
									as={Input}
									name="purpose"
									placeholder="ປ້ອນຈຸດປະສົງ..."
									type="text"
								/>
							</div>
							<div>
								<Label>ມີຮາກຖານຂຶ້ນກັບອົງຄະນະພັກ</Label>
								<Field
									as={Input}
									name="partyCommittee"
									placeholder="ປ້ອນມີຮາກຖານຂຶ້ນກັບອົງຄະນະພັກ..."
									type="text"
								/>
							</div>

							<div className="space-y-3">
								<Label>
									ຂັ້ນຄຸ້ມຄອງ <span className="text-red-500">*</span>
								</Label>
								<RadioGroup
									className="flex items-center gap-4"
									defaultValue={dependOn}
									onValueChange={setDependOn}
								>
									<div className="flex items-center space-x-2">
										<RadioGroupItem value="MINISTRY" id="MINISTRY" />
										<Label htmlFor="MINISTRY">ຂັ້ນສູນກາງ</Label>
									</div>
									<div className="flex items-center space-x-2">
										<RadioGroupItem value="LOCAL" id="LOCAL" />
										<Label htmlFor="LOCAL">ຂັ້ນທ້ອງຖິ່ນ</Label>
									</div>
								</RadioGroup>
							</div>

							{dependOn === "LOCAL" ? (
								<>
									<div>
										<Label htmlFor="dependOfProvinceId">
											ແຂວງ <span className="text-red-500">*</span>
										</Label>
										<Select
											name="dependOfProvinceId"
											onValueChange={(value) => {
												setAddOnValue((prev) => ({
													...prev,
													dependOfProvinceId: value,
												}));
											}}
											value={addOnValue.dependOfProvinceId}
											required
										>
											<SelectTrigger className="w-full">
												<SelectValue placeholder="ເລືອກແຂວງ" />
											</SelectTrigger>
											<SelectContent>
												{province?.map((province) => (
													<SelectItem
														value={province.id}
														key={province.provinceCode}
													>
														{province?.province}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
									<div>
										<Label htmlFor="dependOfDepartmentId">
											ພະແນກ <span className="text-red-500">*</span>
										</Label>
										<Select
											name="dependOfDepartmentId"
											onValueChange={(value) => {
												setAddOnValue((prev) => ({
													...prev,
													dependOfDepartmentId: value,
												}));
											}}
											value={addOnValue.dependOfDepartmentId}
											required
										>
											<SelectTrigger className="w-full">
												<SelectValue placeholder="ເລືອກພະແນກ" />
											</SelectTrigger>
											<SelectContent>
												{department?.map((item) => (
													<SelectItem value={item.id} key={item.id}>
														{item?.department}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
								</>
							) : (
								<>
									<div>
										<Label htmlFor="ministryId">
											ກະຊວງ <span className="text-red-500">*</span>
										</Label>
										<Select
											name="ministryId"
											onValueChange={(value) => {
												setAddOnValue((prev) => ({
													...prev,
													ministryId: value,
												}));
											}}
											value={addOnValue.ministryId}
											required
										>
											<SelectTrigger className="w-full">
												<SelectValue placeholder="ເລືອກກະຊວງ" />
											</SelectTrigger>
											<SelectContent>
												{ministry?.map((item) => (
													<SelectItem value={item.id} key={item.id}>
														{item?.ministry}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
								</>
							)}
						</section>

						<section className="space-y-6">
							<header>
								<h3 className="text-[18px] font-semibold">ພາກສ່ວນການຖືຫຸ້ນ</h3>
								<p className="text-muted-foreground text-[14px]">
									ຂໍ້ມູນການຖືຫຸ້ນຂອງວິສາຫະກິດ.
								</p>
								<Separator className="mt-6" />
							</header>

							<div>
								<Label>
									ລັດຖືຫຸ້ນ <span className="text-red-500">*</span>
								</Label>
								<Field
									as={Input}
									name="govShareholding"
									placeholder="ປ້ອນລັດຖືຫຸ້ນ..."
									type="number"
									required
								/>
							</div>
							<div>
								<Label>ເອກະຊົນພາຍໃນຖືຫຸ້ນ</Label>
								<Field
									as={Input}
									name="domShareholding"
									placeholder="ປ້ອນເອກະຊົນພາຍໃນຖືຫຸ້ນ..."
									type="number"
								/>
							</div>
							<div>
								<Label>ເອກະຊົນພາຍນອກຖືຫຸ້ນ</Label>
								<Field
									as={Input}
									name="forShareholding"
									placeholder="ປ້ອນເອກະຊົນພາຍນອກຖືຫຸ້ນ..."
									type="number"
								/>
							</div>
						</section>

						<section className="space-y-6">
							<header>
								<h3 className="text-[18px] font-semibold">ການລົງທຶນ</h3>
								<p className="text-muted-foreground text-[14px]">
									ຂໍ້ມູນລົງທຶນຂອງວິສາຫະກິດ.
								</p>
								<Separator className="mt-6" />
							</header>

							<div>
								<Label htmlFor="coreSectorId">
									ຂະແໜງການ <span className="text-red-500">*</span>
								</Label>
								<Select
									name="coreSectorId"
									onValueChange={(value) => {
										setFieldValue("coreSectorId", value);
									}}
									value={values.coreSectorId}
									required
								>
									<SelectTrigger className="w-full">
										<SelectValue placeholder="ເລືອກຂະແໜງການ" />
									</SelectTrigger>
									<SelectContent>
										{sector?.map((item) => (
											<SelectItem value={item.id} key={item.id}>
												{item?.coreSector}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<div>
								<Label>ທຸລະກິດຫຼັກ</Label>
								<Field
									as={Input}
									name="coreBusiness"
									placeholder="ປ້ອນມີທຸລະກິດຫຼັກ..."
									type="text"
								/>
							</div>
						</section>

						<section className="space-y-6">
							<header>
								<h3 className="text-[18px] font-semibold">ຂໍ້ມູນບັນຊີເຂົ້າສູ່ລະບົບ</h3>
								<p className="text-muted-foreground text-[14px]">
									ເພື່ອໃຫ້ທາງວິສາຫະກິດ ສາມາດເຂົ້າໄປແກ້ໄຂຂໍ້ມູນຕົນເອງໃນລະບົບພາຍຫຼັງ.
								</p>
								<Separator className="mt-6" />
							</header>
							<div>
								<Label>
									ຊື່ຜູ້ໃຊ້ <span className="text-red-500">*</span>
								</Label>
								<Field
									as={Input}
									name="userCode"
									placeholder="ຊື່ຜູ້ໃຊ້"
									type="text"
									required={!parsedData?.id}
								/>
							</div>
							<div>
								<Label>
									ລະຫັດຜ່ານ <span className="text-red-500">*</span>
								</Label>
								<Field
									as={Input}
									name="password"
									placeholder="ລະຫັດຜ່ານ"
									type="password"
									required={!parsedData?.id}
								/>
							</div>
							<div>
								<Label>
									ຢືນຢັນລະຫັດຜ່ານ <span className="text-red-500">*</span>
								</Label>
								<Input
									name="confirmPassword"
									placeholder="ຢືນຢັນລະຫັດຜ່ານ"
									type="password"
									onChange={(e) => setConfirmPassword(e.target.value)}
									required={!parsedData?.id}
									value={confirmPassword}
								/>
							</div>
							{confirmPasswordError ? (
								<small className="text-red-500">ລະຫັດຜ່ານບໍ່ກົງກັນ</small>
							) : null}
						</section>

						<div className="flex justify-end w-full gap-4">
							<Button
								onClick={() => navigate({ to: "..", replace: true })}
								variant={"outline"}
							>
								ຍົກເລີກ
							</Button>
							<Button type="submit" disabled={updateMutation.isPending}>
								{updateMutation.isPending ? "ກຳລັງບັນທຶກຂໍ້ມູນ" : "ບັນທຶກ"}
							</Button>
						</div>
					</form>
				)}
			</Formik>
		</>
	);
}
